import React, { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../config/api";
import { 
  FaTrash, 
  FaPlus, 
  FaSave, 
  FaImage, 
  FaVideo, 
  FaCloudUploadAlt,
  FaSearch, 
  FaInfoCircle, 
  FaFilm,
  FaCheckCircle
} from "react-icons/fa";
import "../styles/ManageGallery.css";
import { resolveGalleryImageSrc } from "../../data/galleryData";

const API = API_ENDPOINTS.GALLERY;

/* ================= YOUTUBE EMBED URL UTILITY ================= */
const getYouTubeEmbedUrl = (url) => {
  if (!url) return "";

  if (url.includes("embed")) return url;

  if (url.includes("youtu.be")) {
    const id = url.split("youtu.be/")[1];
    return `https://www.youtube.com/embed/${id}`;
  }

  if (url.includes("watch?v=")) {
    const id = url.split("watch?v=")[1].split("&")[0];
    return `https://www.youtube.com/embed/${id}`;
  }

  return url;
};

const ManageGallery = () => {
  /* ================= STATE ================= */
  const [header, setHeader] = useState({
    title: "",
    description: "",
  });

  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Custom Redesign UI States
  const [activeTab, setActiveTab] = useState("photos"); // "header" | "photos" | "videos"
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [saveSuccess, setSaveSuccess] = useState(false);

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await fetch(API);
        const data = await res.json();

        setHeader(data.header || { title: "", description: "" });
        setPhotos(data.photos || []);
        setVideos(data.videos || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  /* ================= UPLOAD IMAGE ================= */
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("image", file);

    const res = await fetch(API_ENDPOINTS.UPLOAD, {
      method: "POST",
      body: formData,
    });

    const data = await res.json();
    return data.imageUrl;
  };

  /* ================= PHOTO ACTIONS ================= */
  const handlePhotoImage = (index, file) => {
    if (!file) return;

    const updated = [...photos];
    updated[index].file = file;
    updated[index].preview = URL.createObjectURL(file);
    setPhotos(updated);
  };

  const updatePhoto = (index, field, value) => {
    const updated = [...photos];
    updated[index][field] = value;
    setPhotos(updated);
  };

  const addPhoto = () => {
    setPhotos([
      {
        id: Date.now(),
        title: "",
        category: "temple",
        src: "",
        preview: "",
        file: null,
      },
      ...photos,
    ]);
    setActiveTab("photos");
  };

  const deletePhoto = (index) => {
    if (window.confirm("Are you sure you want to delete this photo?")) {
      setPhotos(photos.filter((_, i) => i !== index));
    }
  };

  /* ================= VIDEO ACTIONS ================= */
  const updateVideo = (index, field, value) => {
    const updated = [...videos];
    updated[index][field] = value;

    if (field === "src") {
      updated[index].src = getYouTubeEmbedUrl(value);
    }

    setVideos(updated);
  };

  const addVideo = () => {
    setVideos([
      {
        id: Date.now(),
        title: "",
        category: "aarti",
        src: "",
      },
      ...videos,
    ]);
    setActiveTab("videos");
  };

  const deleteVideo = (index) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      setVideos(videos.filter((_, i) => i !== index));
    }
  };

  /* ================= SAVE CHANGES ================= */
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveSuccess(false);

      /* Upload newly added images first */
      const updatedPhotos = await Promise.all(
        photos.map(async (p) => {
          if (p.file) {
            const url = await uploadImage(p.file);
            return {
              title: p.title,
              category: p.category,
              src: url,
            };
          }
          return {
            title: p.title,
            category: p.category,
            src: p.src,
          };
        })
      );

      /* Clean videos */
      const updatedVideos = videos.map((v) => ({
        title: v.title,
        category: v.category,
        src: getYouTubeEmbedUrl(v.src),
      }));

      const payload = {
        header,
        photos: updatedPhotos,
        videos: updatedVideos,
      };

      await fetch(API, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      alert("🎉 Gallery Saved Successfully!");
    } catch (err) {
      console.error(err);
      alert("❌ Save Failed");
    } finally {
      setIsSaving(false);
    }
  };

  /* ================= RENDER LOADING ================= */
  if (loading) {
    return (
      <div className="gallery-loading">
        <div className="spinner"></div>
        <h2>Loading Gallery Content...</h2>
        <p>Connecting to database</p>
      </div>
    );
  }

  /* ================= FILTER LOGIC ================= */
  const filteredPhotos = photos.filter(photo => {
    const matchesSearch = photo.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const cat = (photo.category || "").toLowerCase();
    const filter = filterCategory.toLowerCase();
    const matchesCategory =
      filter === "all" ||
      cat === filter ||
      (filter === "darshan" && (cat === "temple" || cat === "darshan")) ||
      (filter === "poojas" && (cat === "ritual" || cat === "poojas" || cat === "aarti")) ||
      (filter === "seva" && (cat === "events" || cat === "seva" || cat === "annadhanam")) ||
      (filter === "temple" && (cat === "darshan" || cat === "temple")) ||
      (filter === "ritual" && (cat === "poojas" || cat === "ritual")) ||
      (filter === "events" && (cat === "seva" || cat === "events"));
    return matchesSearch && matchesCategory;
  });

  const filteredVideos = videos.filter(video => {
    const matchesSearch = video.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === "all" || video.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="manage-gallery-container">
      {/* HEADER BANNER */}
      <div className="gallery-banner">
        <div className="banner-content">
          <h1>🖼️ Manage Gallery</h1>
          <p>Organize, upload, and update the temple's media library including photos, videos, and playlists.</p>
        </div>
        <div className="banner-stats">
          <div className="stat-pill">
            <FaImage className="icon" />
            <span><strong>{photos.length}</strong> Photos</span>
          </div>
          <div className="stat-pill">
            <FaVideo className="icon" />
            <span><strong>{videos.length}</strong> Videos</span>
          </div>
        </div>
      </div>

      {/* QUICK ACTIONS & TAB SWITCHERS */}
      <div className="dashboard-controls">
        <div className="tabs-wrapper">
          <button 
            className={`tab-btn ${activeTab === "photos" ? "active" : ""}`}
            onClick={() => { setActiveTab("photos"); setFilterCategory("all"); }}
          >
            <FaImage /> Photos Directory
          </button>
          <button 
            className={`tab-btn ${activeTab === "videos" ? "active" : ""}`}
            onClick={() => { setActiveTab("videos"); setFilterCategory("all"); }}
          >
            <FaVideo /> Videos Directory
          </button>
          <button 
            className={`tab-btn ${activeTab === "header" ? "active" : ""}`}
            onClick={() => setActiveTab("header")}
          >
            <FaInfoCircle /> Header Settings
          </button>
        </div>

        {activeTab !== "header" && (
          <div className="search-filter-box">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder={`Search ${activeTab}...`} 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <select 
              value={filterCategory} 
              onChange={(e) => setFilterCategory(e.target.value)}
              className="category-filter"
            >
              <option value="all">All Categories</option>
              {activeTab === "photos" ? (
                <>
                  <option value="darshan">Temple Darshan</option>
                  <option value="alankaram">Divine Alankaram</option>
                  <option value="poojas">Aarti & Poojas</option>
                  <option value="guruji">Guruji</option>
                  <option value="deities">Sanctum Deities</option>
                  <option value="seva">Seva & Annadhanam</option>
                  <option value="temple">Temple</option>
                </>
              ) : (
                <>
                  <option value="aarti">Aarti</option>
                  <option value="bhajan">Bhajan</option>
                  <option value="festival">Festival</option>
                </>
              )}
            </select>
          </div>
        )}

        <div className="quick-action-buttons">
          {activeTab === "photos" && (
            <button className="action-btn add-btn-primary" onClick={addPhoto}>
              <FaPlus /> Add Photo
            </button>
          )}
          {activeTab === "videos" && (
            <button className="action-btn add-btn-primary" onClick={addVideo}>
              <FaPlus /> Add Video
            </button>
          )}
          <button 
            className={`action-btn save-btn-primary ${isSaving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <div className="spinner-small"></div> Saving...
              </>
            ) : saveSuccess ? (
              <>
                <FaCheckCircle /> Saved!
              </>
            ) : (
              <>
                <FaSave /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* MAIN CONTENT PANELS */}
      <div className="panel-container">
        
        {/* PANEL: HEADER SETTINGS */}
        {activeTab === "header" && (
          <div className="panel-card fade-in">
            <div className="panel-card-header">
              <h3><FaInfoCircle className="title-icon" /> Edit Gallery Details</h3>
              <p>These texts will appear at the top of the public gallery page.</p>
            </div>
            <div className="panel-card-body">
              <div className="form-group">
                <label>Gallery Title</label>
                <input
                  type="text"
                  value={header.title}
                  onChange={(e) => setHeader({ ...header, title: e.target.value })}
                  placeholder="e.g. Divine Darshan Gallery"
                />
              </div>
              <div className="form-group">
                <label>Gallery Description / Subtitle</label>
                <textarea
                  value={header.description}
                  onChange={(e) => setHeader({ ...header, description: e.target.value })}
                  placeholder="Describe the gallery contents or share a quote..."
                  rows={4}
                />
              </div>
            </div>
          </div>
        )}

        {/* PANEL: PHOTOS */}
        {activeTab === "photos" && (
          <div className="media-section fade-in">
            {filteredPhotos.length === 0 ? (
              <div className="empty-state">
                <FaImage className="empty-icon" />
                <h3>No Photos Found</h3>
                <p>{searchQuery || filterCategory !== "all" ? "Try adjusting your filters or search terms." : "Start by adding your first temple photo."}</p>
                <button className="action-btn add-btn-primary" onClick={addPhoto}>
                  <FaPlus /> Add Photo Now
                </button>
              </div>
            ) : (
              <div className="media-grid">
                {filteredPhotos.map((photo, index) => (
                  <div className="media-card" key={photo.id || index}>
                    <div className="media-preview-container">
                      {photo.preview || photo.src ? (
                        <img
                          src={photo.preview || resolveGalleryImageSrc(photo.src)}
                          alt={photo.title || "Preview"}
                          className="media-img"
                          onError={(e) => {
                            const raw = photo.src || "";
                            const baseName = raw.split("-").pop() || raw.split("/").pop();
                            if (baseName && !e.target.src.endsWith(`/images/${baseName}`)) {
                              e.target.src = `/images/${baseName}`;
                            } else {
                              e.target.src = "/images/placeholder.jpg";
                            }
                          }}
                        />
                      ) : (
                        <div className="no-media-placeholder">
                          <FaImage className="icon" />
                          <span>No Image Uploaded</span>
                        </div>
                      )}
                      <div className="card-badge">
                        {photo.category.toUpperCase()}
                      </div>
                      <button 
                        className="delete-icon-btn" 
                        onClick={() => deletePhoto(index)}
                        title="Delete photo"
                      >
                        <FaTrash />
                      </button>
                    </div>

                    <div className="media-card-details">
                      <div className="form-group-compact">
                        <label>Photo Title</label>
                        <input
                          type="text"
                          value={photo.title}
                          onChange={(e) => updatePhoto(index, "title", e.target.value)}
                          placeholder="Enter title..."
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group-compact">
                          <label>Category</label>
                          <select
                            value={photo.category}
                            onChange={(e) => updatePhoto(index, "category", e.target.value)}
                          >
                            <option value="darshan">Temple Darshan</option>
                            <option value="alankaram">Divine Alankaram</option>
                            <option value="poojas">Aarti & Poojas</option>
                            <option value="guruji">Guruji</option>
                            <option value="deities">Sanctum Deities</option>
                            <option value="seva">Seva & Annadhanam</option>
                            <option value="temple">Temple</option>
                          </select>
                        </div>

                        <div className="form-group-compact flex-grow">
                          <label>File Upload</label>
                          <label className="custom-file-upload">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handlePhotoImage(index, e.target.files[0])}
                            />
                            <FaCloudUploadAlt className="icon" />
                            <span>{photo.file ? photo.file.name : "Choose Image"}</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PANEL: VIDEOS */}
        {activeTab === "videos" && (
          <div className="media-section fade-in">
            {filteredVideos.length === 0 ? (
              <div className="empty-state">
                <FaFilm className="empty-icon" />
                <h3>No Videos Found</h3>
                <p>{searchQuery || filterCategory !== "all" ? "Try adjusting your filters or search terms." : "Start by adding your first video link."}</p>
                <button className="action-btn add-btn-primary" onClick={addVideo}>
                  <FaPlus /> Add Video Now
                </button>
              </div>
            ) : (
              <div className="media-grid">
                {filteredVideos.map((video, index) => (
                  <div className="media-card" key={video.id || index}>
                    <div className="media-preview-container">
                      {video.src ? (
                        <iframe
                          src={video.src}
                          title={video.title}
                          allowFullScreen
                          className="media-iframe"
                        />
                      ) : (
                        <div className="no-media-placeholder video">
                          <FaVideo className="icon" />
                          <span>Enter YouTube Link below</span>
                        </div>
                      )}
                      <div className="card-badge video">
                        {video.category.toUpperCase()}
                      </div>
                      <button 
                        className="delete-icon-btn" 
                        onClick={() => deleteVideo(index)}
                        title="Delete video"
                      >
                        <FaTrash />
                      </button>
                    </div>

                    <div className="media-card-details">
                      <div className="form-group-compact">
                        <label>Video Title</label>
                        <input
                          type="text"
                          value={video.title}
                          onChange={(e) => updateVideo(index, "title", e.target.value)}
                          placeholder="Enter title..."
                        />
                      </div>

                      <div className="form-group-compact">
                        <label>YouTube Link</label>
                        <input
                          type="text"
                          value={video.src}
                          onChange={(e) => updateVideo(index, "src", e.target.value)}
                          placeholder="e.g. https://www.youtube.com/watch?v=..."
                        />
                      </div>

                      <div className="form-group-compact">
                        <label>Category</label>
                        <select
                          value={video.category}
                          onChange={(e) => updateVideo(index, "category", e.target.value)}
                        >
                          <option value="aarti">Aarti</option>
                          <option value="bhajan">Bhajan</option>
                          <option value="festival">Festival</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* FLOATING ACTION BAR FOR SAVE */}
      <div className="floating-save-bar">
        <div className="save-bar-info">
          <span>Unsaved changes will be lost unless saved.</span>
        </div>
        <button 
          className={`action-btn save-btn-primary ${isSaving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <div className="spinner-small"></div> Saving changes...
            </>
          ) : saveSuccess ? (
            <>
              <FaCheckCircle /> Saved Successfully!
            </>
          ) : (
            <>
              <FaSave /> Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ManageGallery;
